import type { CartWithItems, OrderRead, Product, ProductRead, ShippingOptionRead, ChargeTokenRequest } from '@shared/types'
import type { ShippingForm } from '@/types'
import bundles from '@/data/bundles'
import eventsData from '@/data/events'
import products from '@/data/products'
import subscriptions from '@/data/subscriptions'
import { getTime } from '@/utils'
import {
	trackAddShippingInfo,
	trackAddToCart,
	trackBeginCheckout,
	trackRemoveFromCart,
} from '@/utils/analytics'
import { ecommerce, payment } from '@shared/api'
import { defineStore } from 'pinia'

interface StoreState {
	cartId: string
	orderId: string
	activeOrder?: OrderRead
	cart?: CartWithItems
	isLoading: boolean
	isInitialized: boolean
	shippingInformation: ShippingForm
	postSellProducts: Product[]
	shippingOptions: ShippingOptionRead[]
	maintenanceMode: boolean
	events: ProductRead[]
	apiSubscriptions: ProductRead[]
}

export const useStore = defineStore('cart', {
	state: (): StoreState => ({
		cartId: '',
		orderId: '',
		activeOrder: undefined,
		cart: undefined,
		isLoading: false,
		isInitialized: false,
		shippingInformation: {} as ShippingForm,
		postSellProducts: [],
		shippingOptions: [],
		maintenanceMode: false,
		events: eventsData as ProductRead[],
		apiSubscriptions: [],
	}),

	getters: {
		allProducts(): Product[] {
			return (products.filter(product => product.visibility !== 'Unlisted') as Product[])
				.sort((a: Product, b: Product) => getTime(b.release_date) - getTime(a.release_date))
		},

		visibleProducts(): ProductRead[] {
			return this.allProducts.filter(product => product.visibility === 'Public' && !product.more_info?.postsell && product.isbn) as ProductRead[]
		},

		allBundles() {
			return bundles
		},

		subscriptionOptions(): ProductRead[] {
			// זמנית - חזרה לנתונים הסטטיים בלבד עד שנפתור את בעיות הטיפוס
			//
			// ⚠️ שים לב לפני שמחזירים טעינה מהשרת (apiSubscriptions):
			// מסך המנויים ב-SubscriptionOptions.vue מסתמך על שדות שמוגדרים כרגע
			// רק בקובץ הסטטי src/data/subscriptions.ts ולא בהכרח מגיעים מהשרת:
			//   - more_info.feature_ids  -> קובע אילו רוביקות (V) מופיעות לכל מסלול
			//   - more_info.external_link -> מסלול עם לינק חיצוני ("שומר סף ושיבולת פלוס")
			//   - המסלול "שומר סף ושיבולת פלוס" עצמו (לא קיים בשרת)
			// אם עוברים ל-apiSubscriptions, יש לוודא שהשרת מחזיר את השדות והמסלול האלה,
			// אחרת הרוביקות לא יסומנו, הלינק החיצוני לא יעבוד, והמסלול ייעלם.
			return subscriptions
				.filter(product => product.product_type === 'Subscription' && product.visibility === 'Public')
				.sort((a, b) => (a.price ?? 0) - (b.price ?? 0)) as ProductRead[]
		},

		upcomingEvents(): ProductRead[] {
			const today = new Date()
			today.setHours(0, 0, 0, 0)
			return this.events.filter(event => {
				if (!event.more_info?.date) return true
				const eventDate = new Date(event.more_info.date)
				return eventDate >= today
			})
		},

		cartItems(state): any[] {
			return state.cart?.items.filter((item: any) => item.product.product_type === 'Physical') ?? []
		},

		cartSubscriptions(state): any[] {
			return state.cart?.items.filter((item: any) => item.product.product_type === 'Subscription') ?? []
		},

		cartEvents(state): any[] {
			return state.cart?.items.filter((item: any) => item.product.product_type === 'Event') ?? []
		},
	},

	actions: {
		/**
		 * Clears the cart ID from state and localStorage when cart is no longer active
		 */
		clearCartId() {
			this.cartId = ''
			this.cart = undefined
			localStorage.removeItem('cartId')
		},

		/**
		 * Saves cartId to localStorage
		 */
		persistCartId() {
			if (this.cartId) {
				localStorage.setItem('cartId', this.cartId)
			} else {
				localStorage.removeItem('cartId')
			}
		},

		/**
		 * Checks if an error indicates the cart is no longer active
		 */
		isCartInactiveError(error: any): boolean {
			const status = error?.response?.status
			return status === 404 || status === 400 || status === 410
		},

		async loadShippingOptions() {
			try {
				const { data } = await ecommerce.shippingOptions.get()
				this.shippingOptions = data
			} catch (error) {
				console.error('Failed to load shipping options', error)
			}
		},

		async updateShippingOption(shippingOptionId?: string) {
			if (!this.cartId || !shippingOptionId) return

			try {
				const freeShipping = shippingOptionId === '0'
				const shippingOption = freeShipping ? undefined : shippingOptionId
				const { data } = await ecommerce.cart.shipping({ cartId: this.cartId, freeShipping, shippingOptionId: shippingOption })
				this.setCart(data)
			} catch (error: any) {
				console.error('Failed to update shipping option', error)
				if (this.isCartInactiveError(error)) {
					this.clearCartId()
				}
			}
		},

		async initCart() {
			if (this.isInitialized || this.isLoading) return
			this.isLoading = true
			try {
				// Only restore cartId from localStorage
				const savedCartId = localStorage.getItem('cartId')
				if (savedCartId) {
					this.cartId = savedCartId
				}

				await this.getCart()
				await this.loadShippingOptions()
				await this.loadEvents()
				await this.loadSubscriptions()
			} finally {
				this.isLoading = false
				this.isInitialized = true
			}
		},

		async applyCoupon(code: string) {
			if (!code || !this.cartId) return false

			try {
				const { data } = await ecommerce.cart.applyCoupon({ cartId: this.cartId, couponCode: code })
				if (data) {
					this.setCart(data)
					return true
				}
				return false
			} catch (error: any) {
				console.error('Failed to apply coupon', error)
				// Only clear cart if it's actually inactive (404, 410)
				// Don't clear on 400 (bad request/invalid coupon)
				if (this.isCartInactiveError(error)) {
					// Reload cart to verify it's actually gone
					await this.getCart()
					if (!this.cart) {
						this.clearCartId()
					}
				}
				return false
			}
		},

		async removeCoupon() {
			if (!this.cartId) return

			try {
				const { data } = await ecommerce.cart.removeCoupon({ cartId: this.cartId })
				if (data) this.setCart(data)
			} catch (error: any) {
				console.error('Failed to remove coupon', error)
				if (this.isCartInactiveError(error)) {
					this.clearCartId()
				}
			}
		},

		async shippingCalc() {
			const defaultShippingOption = this.shippingOptions.find(option => option.is_default)?.id

			if (!this.cart?.items.length) {
				return this.updateShippingOption(defaultShippingOption)
			}

			const hasSubscriptions = this.cart.items.some(item => item.product.product_type === 'Subscription')
			const hasOnlyEvents = this.cart.items.length > 0 && this.cart.items.every(item => item.product.product_type === 'Event')
			const hasOnlyDigitalProducts = this.cart.items.length > 0 && this.cart.items.every(item =>
				item.product.product_type === 'Digital' ||
				item.product.product_type === 'Event' ||
				item.product.product_type === 'Subscription'
			)

			// פטור ממשלוח לפי מוצר: מספיק שפריט אחד בעגלה מסומן ב-more_info.free_shipping
			// כדי לפטור את כל ההזמנה מדמי משלוח. לסימון ספר בודד: הוסף למוצר ב-data/products.ts
			//   "more_info": { "free_shipping": true }
			const hasFreeShippingItem = this.cart.items.some(item => item.product.more_info?.free_shipping === true)

			const isFree = hasSubscriptions || hasOnlyEvents || hasOnlyDigitalProducts || hasFreeShippingItem

			return this.updateShippingOption(isFree ? '0' : defaultShippingOption)
		},

		async addToCart(item: Product) {
			if (this.productInCart(item.id)) return

			this.isLoading = true
			try {
				// Create cart if it doesn't exist
				if (!this.cartId) {
					const { data } = await ecommerce.cart.post()
					if (!data) return
					this.cartId = data.id
					this.persistCartId()
				}

				// If adding a subscription, remove existing subscriptions first
				if (item.product_type === 'Subscription') {
					const subscriptionItems = this.cart?.items.filter(i => i.product.product_type === 'Subscription') || []
					for (const subItem of subscriptionItems) {
						await this.removeFromCart(subItem.id)
					}
				}

				// Add item to cart
				await ecommerce.cart.item.post({ cartId: this.cartId, productId: item.id })
				await this.getCart()
				await this.shippingCalc()

				// Track analytics
				trackAddToCart(item, 1)
			} catch (error: any) {
				console.error('Failed to add item to cart', error)
				if (this.isCartInactiveError(error)) {
					this.clearCartId()
				}
				// Refresh cart to get current state
				if (this.cartId) {
					await this.getCart()
				}
			} finally {
				this.isLoading = false
			}
		},

		async updateQuantity(itemId: string, quantity: number) {
			if (!this.cartId) return

			try {
				await ecommerce.cart.item.put({ cartId: this.cartId, itemId, quantity })
				await this.getCart()
				await this.shippingCalc()
			} catch (error: any) {
				console.error('Failed to update item quantity', error)
				if (this.isCartInactiveError(error)) {
					this.clearCartId()
				}
			}
		},

		setCart(data: CartWithItems) {
			const cartId = data.id || ''
			this.cartId = cartId
			this.cart = cartId ? data : undefined
			this.persistCartId()
			this.isInitialized = true

			this.shippingInformation = {
				first_name: data.first_name || '',
				last_name: data.last_name || '',
				email: data.email || '',
				phone: data.phone || '',
				company: data.company || '',
				street_address_1: data.shipping_street_address || '',
				street_address_2: data.shipping_street_address_2 || '',
				building_number: data.shipping_building_number || '',
				apartment_number: data.shipping_apartment_number || '',
				floor: data.shipping_floor || '',
				city: data.shipping_city || '',
				state: data.shipping_state || '',
				zip_code: data.shipping_postal_code || '',
				country: data.shipping_country || '',
				more_info: {
					entrance_code: '',
					marketing: false,
				},
				tos: true,
			}

		},

		async getCart() {
			if (!this.cartId) return

			this.isLoading = true
			try {
				const { data } = await ecommerce.cart.get({ cartId: this.cartId })
				this.setCart(data)
			} catch (error: any) {
				console.error('Failed to get cart', error)
				if (this.isCartInactiveError(error)) {
					this.clearCartId()
				}
			} finally {
				this.isLoading = false
			}
		},

		async removeFromCart(itemId: string) {
			if (!this.cartId) return

			const removedItem = this.cart?.items.find(item => item.id === itemId || item.product.id === itemId)
			if (!removedItem) return

			try {
				await ecommerce.cart.item.delete({ cartId: this.cartId, itemId: removedItem.id })
				await this.getCart()
				await this.shippingCalc()

				trackRemoveFromCart(removedItem.product, removedItem.quantity)
			} catch (error: any) {
				console.error('Failed to remove item from cart', error)
				if (this.isCartInactiveError(error)) {
					this.clearCartId()
				}
			}
		},

		async clearCart() {
			if (!this.cartId || !this.cart?.items.length) return

			const itemsToRemove = [...this.cart.items]

			try {
				await Promise.all(
					itemsToRemove.map(item => ecommerce.cart.item.delete({ cartId: this.cartId, itemId: item.id }))
				)
				await this.getCart()
			} catch (error: any) {
				console.error('Failed to clear cart', error)
				if (this.isCartInactiveError(error)) {
					this.clearCartId()
				}
			}
		},

		async getOrder(orderId: string) {
			const { data } = await ecommerce.order.get({ orderId })
			if (data) this.activeOrder = data
		},

		productInCart(productId: string) {
			if (!this.cart) return false
			return !!this.cart.items.find(item => item.product.id === productId)
		},

		async updateShippingInfo() {
			if (!this.cartId) return
			this.shippingInformation.state = 'ישראל'
			this.shippingInformation.country = 'ישראל'

			try {
				await ecommerce.cart.shippingInfo({ cartId: this.cartId, shippingInformation: this.shippingInformation })

				if (this.cart?.items) {
					trackAddShippingInfo(this.cart.items, this.cart.total)
				}
			} catch (error: any) {
				console.error('Failed to update shipping info', error)
				if (this.isCartInactiveError(error)) {
					this.clearCartId()
				}
			}
		},

		async checkout() {
			if (!this.cartId) return null

			try {
				if (this.cart?.items) {
					trackBeginCheckout(this.cart.items, this.cart.total)
				}

				const { data } = await ecommerce.cart.checkout({ cartId: this.cartId })
				this.orderId = data.id
				this.activeOrder = data

				// Clear the cart after successful checkout
				this.clearCartId()

				this.router.push(`/payment/${data.id}`)
				return data
			} catch (error: any) {
				console.error('Checkout failed', error)
				if (this.isCartInactiveError(error)) {
					this.clearCartId()
				}
				throw error
			}
		},

		async postSellCheckout(products: Product[], person_id: string, payment_token_uid: string) {
			console.log({'postSellCheckout': {products, person_id, payment_token_uid}})
			if (!products || products.length === 0) throw new Error('No products provided for upsell checkout')
			if (!person_id) throw new Error('Person ID is required for upsell checkout')
			if (!payment_token_uid)	throw new Error('Payment token is required for upsell checkout')

			try {
				this.isLoading = true

				const { data: cartData } = await ecommerce.cart.post({ customerId: person_id })
				if (!cartData) {
					throw new Error('Failed to create cart')
				}

				const newCartId = cartData.id

				for (const product of products) {
					await ecommerce.cart.item.post({ cartId: newCartId, productId: product.id, quantity: 1 })
				}
				const couponCode = '4_books_for_100'
				await ecommerce.cart.applyCoupon({ cartId: newCartId, couponCode })

				const { data: cartWithItems } = await ecommerce.cart.get({ cartId: newCartId })
				if (!cartWithItems) {
					throw new Error('Failed to retrieve cart with items')
				}
				if (this.shippingInformation.first_name) {
					await ecommerce.cart.shippingInfo({ cartId: newCartId, shippingInformation: this.shippingInformation })
				}

				const { data: updatedCart } = await ecommerce.cart.get({ cartId: newCartId })
				if (updatedCart) {
					this.setCart(updatedCart)
					await this.shippingCalc()
				}

				const { data: orderData } = await ecommerce.cart.checkout({ cartId: newCartId })
				if (!orderData) {
					throw new Error('Failed to create order')
				}

				const chargeRequest: ChargeTokenRequest = {
					amount: orderData.total ?? 0,
					currency: 'ils',
					description: `סלע מאיר, הזדמנות רכישה - ${products.length} ספרים`,
					statement_descriptor: 'סלע מאיר, הזדמנות רכישה',
					payer_info: {
						first_name: orderData.first_name,
						last_name: orderData.last_name,
						email: orderData.email,
						phone: orderData.phone,
						billing_address_line1: orderData.shipping_street_address,
						billing_address_line2: orderData.shipping_street_address_2,
						billing_city: orderData.shipping_city,
						billing_state: orderData.shipping_state,
						billing_postal_code: orderData.shipping_postal_code,
						billing_country: orderData.shipping_country,
					},
					meta: {
						order_id: orderData.id,
						cart_id: newCartId,
						product_ids: products.map(p => p.id),
						product_titles: products.map(p => p.title),
						is_upsell: true,
					}
				}

				const { data: chargeResponse } = await payment.charge({ paymentMethodId: payment_token_uid, chargeTokenRequest: chargeRequest })
				if (!chargeResponse.success) {
					throw new Error('Payment failed')
				}
				await ecommerce.order.put({
					orderId: orderData.id, orderUpdate: {
					status: 'Paid',
					paid_at: new Date().toISOString()
				}
				})

				this.cart = undefined
				this.cartId = ''

				return {
					success: true,
					order: orderData,
					transaction: chargeResponse,
					message: 'Upsell purchase completed successfully'
				}

			} catch (error) {
				console.error('Upsell checkout failed:', error)
				throw error
			} finally {
				this.isLoading = false
			}
		},

		async loadEvents() {
			try {
				const sortedStaticEvents = (eventsData as ProductRead[]).sort((a, b) => {
					const dateA = new Date(a.more_info?.date || a.release_date || 0).getTime()
					const dateB = new Date(b.more_info?.date || b.release_date || 0).getTime()
					return dateB - dateA
				})
				this.events = sortedStaticEvents

				const { data } = await ecommerce.listProducts()
				const apiEvents = data.filter(product => product.product_type === 'Event' && product.featured === true) as ProductRead[]
				if (apiEvents.length > 0) {
					const sortedApiEvents = apiEvents.sort((a, b) => {
						const dateA = new Date(a.more_info?.date || a.release_date || 0).getTime()
						const dateB = new Date(b.more_info?.date || b.release_date || 0).getTime()
						return dateB - dateA
					})
					this.events = sortedApiEvents
				}
			} catch (error) {
				console.error('Failed to load events from API, using static data', error)
				const sortedStaticEvents = (eventsData as ProductRead[]).sort((a, b) => {
					const dateA = new Date(a.more_info?.date || a.release_date || 0).getTime()
					const dateB = new Date(b.more_info?.date || b.release_date || 0).getTime()
					return dateB - dateA
				})
				this.events = sortedStaticEvents
			}
		},

		async loadSubscriptions() {
			try {
				const { data } = await ecommerce.listProducts()
				const apiSubscriptions = data.filter(product =>
					product.product_type === 'Subscription' &&
					product.visibility === 'Public'
				) as ProductRead[]

				if (apiSubscriptions.length > 0) {
					this.apiSubscriptions = apiSubscriptions.sort((a, b) => (a.price ?? 0) - (b.price ?? 0))
					console.log('Loaded subscriptions from API:', this.apiSubscriptions.length)
				}
			} catch (error) {
				console.error('Failed to load subscriptions from API, using static data', error)
				// שירד על הנתונים הסטטיים אם יש בעיה ב-API
				this.apiSubscriptions = []
			}
		},
	},
})
